// Fills the Paradise-Performance deal packet templates (AcroForm PDFs in
// /public/forms/deal-packets/) with deal data and hands the result to the
// browser. The filled PDFs keep live form fields so anything can still be
// adjusted in a PDF viewer before printing — nothing is flattened.

import { DEAL_PACKETS, buildFieldValues } from '../config/dealPacketForms';

const templateCache = new Map();

async function loadTemplateBytes(packet) {
  if (templateCache.has(packet.id)) return templateCache.get(packet.id);
  const res = await fetch(packet.file);
  if (!res.ok) {
    throw new Error(`Could not load ${packet.label} template (${res.status}). Was public/forms/deal-packets deployed?`);
  }
  const bytes = await res.arrayBuffer();
  templateCache.set(packet.id, bytes);
  return bytes;
}

export function getPacket(packetId) {
  const packet = DEAL_PACKETS.find((p) => p.id === packetId);
  if (!packet) throw new Error(`Unknown deal packet: ${packetId}`);
  return packet;
}

// The templates size each field to the glyphs the DMS originally printed
// there, so a longer value (e.g. "F-150" where "M3" once sat) would clip.
// Keep the template's font size as the maximum and shrink just enough to fit
// the field's narrowest widget.
function shrinkToFit(field, text, font) {
  let widgets;
  try {
    widgets = field.acroField.getWidgets();
  } catch {
    return;
  }
  if (!widgets || widgets.length === 0) return;
  const minWidth = Math.min(...widgets.map((w) => w.getRectangle().width)) - 2.5;
  if (!(minWidth > 0)) return;
  const da = field.acroField.getDefaultAppearance() || '';
  const daSize = parseFloat((da.match(/\/\S+\s+([\d.]+)\s+Tf/) || [])[1]) || 8;
  const width = font.widthOfTextAtSize(text, daSize);
  if (width <= minWidth) return;
  const size = Math.max((daSize * minWidth) / width, 4);
  field.setFontSize(Math.floor(size * 10) / 10);
}

/**
 * Fill a packet template with values from the deal model.
 * Returns { bytes, filled, missing } — `missing` lists mapped fields the
 * template didn't contain (should stay empty; useful as a canary).
 */
export async function fillPacket(packetId, dealModel) {
  const packet = getPacket(packetId);
  const [{ PDFDocument, StandardFonts, ParseSpeeds }, templateBytes] = await Promise.all([
    import('pdf-lib'),
    loadTemplateBytes(packet),
  ]);

  // Parse and serialize at full speed. pdf-lib's default throttling yields to
  // the event loop every ~100 objects, and Chrome clamps those timer ticks to
  // ~1s in hidden/backgrounded tabs — turning a <1s fill into many minutes.
  const pdf = await PDFDocument.load(templateBytes, { parseSpeed: ParseSpeeds.Fastest });
  const form = pdf.getForm();
  const available = new Map(form.getFields().map((f) => [f.getName(), f]));
  const { TextAlignment } = await import('pdf-lib');

  const values = buildFieldValues(packetId, dealModel);
  const helvetica = await pdf.embedFont(StandardFonts.Helvetica);
  const filled = [];
  const missing = [];
  for (const [name, value] of Object.entries(values)) {
    const field = available.get(name);
    if (!field) {
      // Shared identity fields (buyer_*, vehicle_*, ...) only exist in some
      // packets — skipping them is normal. A missing position-mapped money
      // cell (..._pN_N) would mean template and mapping are out of sync.
      if (/_p\d+_\d+$/.test(name)) missing.push(name);
      continue;
    }
    if (typeof field.setText !== 'function') continue; // only text fields exist in these templates
    const text = value == null ? '' : String(value);
    field.setText(text);
    if (text) {
      // Money/date/rate cells were right-anchored by the DMS; right-aligning
      // keeps them at the original spot while using the widened rect space.
      if (/_p\d+_\d+$/.test(name) && !name.startsWith('recall_')) {
        try { field.setAlignment(TextAlignment.Right); } catch { /* non-text field */ }
      }
      shrinkToFit(field, text, helvetica);
    }
    filled.push(name);
  }

  form.updateFieldAppearances(helvetica);

  const bytes = await pdf.save({ objectsPerTick: Infinity });
  return { bytes, filled, missing, packet };
}

function packetFileName(packet, dealModel) {
  const last = (dealModel.buyer.lastName || dealModel.buyer.firstName || 'deal')
    .replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'deal';
  const stock = (dealModel.vehicle.stockNumber || '').replace(/[^a-z0-9]+/gi, '').toLowerCase();
  const date = (dealModel.saleDate || new Date().toISOString().slice(0, 10)).replace(/-/g, '');
  return [packet.id, last, stock, date].filter(Boolean).join('_') + '.pdf';
}

export async function downloadPacket(packetId, dealModel) {
  const result = await fillPacket(packetId, dealModel);
  const blob = new Blob([result.bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = packetFileName(result.packet, dealModel);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
  return result;
}

export async function openPacket(packetId, dealModel) {
  const result = await fillPacket(packetId, dealModel);
  const blob = new Blob([result.bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return result;
}
